import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ExternalLink, Check, Loader2 } from 'lucide-react';
import { coworkApi } from '../api/coworkApi';

// `available: false` channels aren't wired to a connect flow yet — they show a
// muted "Soon" pill instead of an actionable Connect button, so a CMO doesn't
// click into a dead end.
const SUPPORTED_CHANNELS = [
  { provider: 'google-ads',  label: 'Google Ads',         category: 'Paid',     icon: '🔵', viewKey: 'google-ads' as const, available: true },
  { provider: 'meta-ads',    label: 'Meta Ads',           category: 'Paid',     icon: '🔷' },
  { provider: 'tiktok-ads',  label: 'TikTok Ads',         category: 'Paid',     icon: '🎵', available: false },
  { provider: 'linkedin',    label: 'LinkedIn',           category: 'Social',   icon: '💼', available: true },
  { provider: 'instagram',   label: 'Instagram',          category: 'Social',   icon: '📸', available: false },
  { provider: 'twitter',     label: 'X / Twitter',        category: 'Social',   icon: '🐦', available: false },
  { provider: 'tiktok',      label: 'TikTok',             category: 'Social',   icon: '🎬', available: false },
  { provider: 'ga4',         label: 'Google Analytics 4', category: 'Analytics', icon: '📊', available: true },
  { provider: 'klaviyo',     label: 'Klaviyo',            category: 'Email/CRM', icon: '✉️', available: false },
  { provider: 'hubspot',     label: 'HubSpot',            category: 'Email/CRM', icon: '🟠', available: false },
  { provider: 'gmail',       label: 'Gmail',              category: 'Email/CRM', icon: '✉️', available: false },
  { provider: 'slack',       label: 'Slack',              category: 'Comms',     icon: '💬', available: true },
];

interface Integration {
  id: string;
  provider: string;
  profileName?: string | null;
  profileImage?: string | null;
  createdAt?: string;
}

interface IntegrationsPageProps {
  onOpenGoogleAds: () => void;
}

/**
 * Meta uses the team-scoped ad-account flow. Keeping this link separate from
 * the legacy Cowork integrations API prevents a browser session token from
 * being sent through an OAuth URL.
 */
function MetaAdAccountsCard() {
  const { t } = useTranslation();

  return (
    <div
      className="flex items-center gap-3 p-3 rounded-lg border border-[#eaecf0] hover:border-[#be43ff]/30 transition-colors"
      data-testid="canonical-meta-ad-accounts"
    >
      <div className="text-[20px] w-8 h-8 flex items-center justify-center bg-[#f8fafc] rounded-md" aria-hidden>
        🔷
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-semibold text-[#101828]">{t('adAccountsPage.canonicalMeta.title')}</div>
        <div className="text-[11px] text-[#94a3b8]">{t('adAccountsPage.canonicalMeta.description')}</div>
      </div>
      <Link
        to="/ad-accounts"
        className="inline-flex items-center gap-1 text-[11px] text-[#be43ff] px-2.5 py-1 rounded-md hover:bg-[#f8f5ff] transition-colors"
        style={{ fontWeight: 600 }}
        aria-label={t('adAccountsPage.canonicalMeta.manageAria')}
      >
        {t('adAccountsPage.canonicalMeta.manage')}
        <ExternalLink className="w-3 h-3" aria-hidden />
      </Link>
    </div>
  );
}

export function IntegrationsPage({ onOpenGoogleAds }: IntegrationsPageProps) {
  const [items, setItems] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(true);
  const [disconnecting, setDisconnecting] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const refresh = useCallback(() => {
    setLoading(true);
    coworkApi.integrations.list()
      .then(d => setItems(d.integrations || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Re-check after returning from any OAuth callback
  useEffect(() => {
    const params = new URLSearchParams(location.search);

    // The former Cowork Meta OAuth callback is intentionally retired. Send any
    // old callback to the team-scoped flow instead of loading legacy state.
    if (params.has('meta_connected') || params.has('meta_error')) {
      navigate('/ad-accounts', { replace: true });
      return;
    }

    const consumed: string[] = [];

    if (params.get('gads_connected') === 'true' || params.get('integration_success') || params.get('slack_connected') === 'true') {
      refresh();
      ['gads_connected', 'integration_success', 'slack_connected', 'brandId', 'profile'].forEach(k => consumed.push(k));
    }
    if (params.get('integration_error') || params.get('gads_error') || params.get('slack_error')) {
      alert(`Integration failed: ${params.get('integration_error') || params.get('gads_error') || params.get('slack_error')}`);
      ['integration_error', 'gads_error', 'slack_error'].forEach(k => consumed.push(k));
    }

    if (consumed.length) {
      consumed.forEach(k => params.delete(k));
      navigate(
        {
          pathname: location.pathname,
          search: params.size ? `?${params.toString()}` : '',
        },
        { replace: true },
      );
    }
  }, [location.pathname, location.search, navigate, refresh]);

  const handleConnect = (provider: string) => {
    if (provider === 'google-ads') {
      window.location.href = coworkApi.integrations.googleAdsConnectUrl();
    } else if (provider === 'linkedin') {
      window.location.href = coworkApi.integrations.connectUrl();
    } else if (provider === 'slack') {
      window.location.href = coworkApi.slack.connectUrl();
    } else if (provider === 'ga4') {
      window.location.href = coworkApi.ga4.connectUrl();
    } else {
      alert(`Connection flow for ${provider} coming soon.`);
    }
  };

  const handleDisconnect = async (integration: Integration) => {
    if (!confirm(`Disconnect ${integration.profileName || integration.provider}? Your agents will lose access to this account.`)) return;
    setDisconnecting(integration.id);
    try {
      await coworkApi.integrations.delete(integration.id);
      setItems(prev => prev.filter(i => i.id !== integration.id));
    } catch (err) {
      alert(`Failed to disconnect: ${(err as Error).message}`);
    } finally {
      setDisconnecting(null);
    }
  };

  const grouped = SUPPORTED_CHANNELS.reduce<Record<string, typeof SUPPORTED_CHANNELS>>((acc, c) => {
    (acc[c.category] ||= []).push(c);
    return acc;
  }, {});

  const byProvider = items.reduce<Record<string, Integration>>((acc, i) => {
    acc[i.provider] = i;
    return acc;
  }, {});

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="px-6 py-4 border-b border-[#eaecf0]">
        <h1 className="text-[18px] font-semibold text-[#101828]">Channels & integrations</h1>
        <p className="text-[12px] text-[#667085] mt-0.5">Connect the platforms your agents work across.</p>
      </div>

      <div className="flex-1 overflow-auto px-6 py-6">
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-5 h-5 animate-spin text-[#94a3b8]" /></div>
        ) : (
          <div className="flex flex-col gap-6 max-w-4xl">
            {Object.entries(grouped).map(([category, channels]) => (
              <div key={category}>
                <div className="text-[11px] uppercase tracking-wider text-[#94a3b8] mb-2 font-semibold">{category}</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {channels.map((c) => {
                    if (c.provider === 'meta-ads') {
                      return <MetaAdAccountsCard key={c.provider} />;
                    }

                    const integration = byProvider[c.provider];
                    const isConnected = !!integration;

                    let subtitle: string;
                    if (isConnected) {
                      subtitle = integration.profileName || 'Connected';
                    } else {
                      subtitle = 'Not connected';
                    }

                    return (
                      <div key={c.provider} className="flex items-center gap-3 p-3 rounded-lg border border-[#eaecf0] hover:border-[#be43ff]/30 transition-colors">
                        {integration?.profileImage ? (
                          <img src={integration.profileImage} alt="" className="w-8 h-8 rounded-md object-cover" />
                        ) : (
                          <div className="text-[20px] w-8 h-8 flex items-center justify-center bg-[#f8fafc] rounded-md">{c.icon}</div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-[13px] font-semibold text-[#101828]">{c.label}</div>
                          <div className="text-[11px] text-[#94a3b8] truncate">{subtitle}</div>
                        </div>

                        {isConnected ? (
                          <span className="flex items-center gap-1 text-[11px] text-[#16a34a] px-2.5 py-1 rounded-md bg-[#f0fdf4]">
                            <Check className="w-3 h-3" />
                            Connected
                          </span>
                        ) : c.available ? (
                          <button
                            onClick={() => handleConnect(c.provider)}
                            className="text-[11px] text-[#be43ff] px-2.5 py-1 rounded-md hover:bg-[#f8f5ff] transition-colors"
                            style={{ fontWeight: 600 }}
                          >
                            Connect
                          </button>
                        ) : (
                          <span className="text-[11px] text-[#94a3b8] px-2.5 py-1 rounded-md bg-[#f8fafc]" style={{ fontWeight: 500 }}>
                            Soon
                          </span>
                        )}

                        {c.viewKey === 'google-ads' && isConnected && (
                          <button
                            onClick={onOpenGoogleAds}
                            className="p-1.5 rounded hover:bg-[#f8f5ff] text-[#be43ff]"
                            title="Open Google Ads agent"
                          >
                            <ExternalLink className="w-3 h-3" />
                          </button>
                        )}

                        {isConnected && integration && (
                          <button
                            onClick={() => handleDisconnect(integration)}
                            disabled={disconnecting === integration.id}
                            className="text-[11px] text-[#94a3b8] hover:text-[#dc2626] px-2 py-1 rounded-md hover:bg-[#fef2f2] transition-colors disabled:opacity-50"
                            title="Disconnect"
                          >
                            {disconnecting === integration.id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Disconnect'}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  );
}
